import React from "react";
import { Pie } from "@ant-design/plots";

// Formatter function
const formatValue = (val, formatType) => {
  if (formatType === "percent") return `${val.toFixed(2)}%`;
  return val < 1000 ? `$${val.toFixed(2)}M` : `$${(val / 1000).toFixed(2)}B`;
};

// Shared DonutChart component
const DonutChart = ({ title, data, formatType, colorMap }) => {
  const totalValue = data.reduce((sum, d) => sum + d.value, 0);

  const config = {
    data,
    angleField: "value",
    colorField: "type",
    radius: 1,
    innerRadius: 0.6,
    color: ({ type }) => colorMap[type] || "#888",

    legend: {
      position: "right",
    },

    tooltip: {
      formatter: ({ type, value }) => ({
        name: type,
        value: formatValue(value, formatType),
      }),
    },

    label: {
      type: "outer",
      formatter: (datum) => {
        const value = datum.value;

        if (formatType === "percent") {
          return `${value.toFixed(1)}%`;
        }

        if (value >= 1e9) {
          return (
            (value / 1e9).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + "B"
          );
        } else if (value >= 1e6) {
          return (
            (value / 1e6).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }) + "M"
          );
        } else {
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        }
      },
      style: {
        fontSize: 15,
        fontWeight: 700,
        color: "#888",
      },
    },

    interactions: [{ type: "element-active" }],
    statistic: {
      title: {
        content: title,
        style: {
          fontSize: 15,
          color: "#555",
        },
      },
      content: {
        content: formatValue(totalValue, formatType),
        style: {
          fontSize: 13,
          color: "#888",
          marginTop: "1%",
        },
      },
    },
  };

  return (
    <div style={{ width: "100%", maxWidth: 500, margin: "0 auto" }}>
      <Pie {...config} />
    </div>
  );
};

// Main component that handles baseline and projected
const PerHazardDonutCharts = ({
  year,
  baselineData,
  projectedData,
  formatType = "currency", // or "percent"
}) => {
  const toChartData = (source) =>
    Object.entries(source || {})
      .filter(([_, val]) => val && val[formatType === "percent" ? 2 : 0] > 0)
      .map(([key, val]) => ({
        type: key,
        value: formatType === "percent" ? val[2] : val[0],
      }));

  const baseline = toChartData(baselineData);
  const projected = toChartData(projectedData);

  const allTypes = Array.from(
    new Set([...baseline, ...projected].map((d) => d.type))
  );

  const colors = ["#1A3670", "#3E63D0", "#98AFF1", "#D5E0FF", "#3A4B6A"];
  const colorMap = {};
  allTypes.forEach((type, index) => {
    colorMap[type] = colors[index % colors.length];
  });

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        gap: 48,
      }}
    >
      <DonutChart
        title="2020"
        data={projected}
        formatType={formatType}
        colorMap={colorMap}
      />
      <DonutChart
        title={year}
        data={baseline}
        formatType={formatType}
        colorMap={colorMap}
      />
    </div>
  );
};

export default PerHazardDonutCharts;
